import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  User, 
  BookOpen,
  Building2,
  Scale,
  Layers
} from 'lucide-react'
import { Logo } from '../ui/Logo'
import { LogoIcon } from '../ui/LogoIcon'
import { ThemeToggle } from '../ui/ThemeToggle'
import { Button } from '../ui/Button'
import { useChat } from '../../contexts/ChatContext'
import { useIsMobile } from '../../hooks/useIsMobile'

interface PublicNavigationProps {
  onChatClick?: () => void
}

export const PublicNavigation: React.FC<PublicNavigationProps> = ({ onChatClick: _onChatClick }) => {
  const { openChat: _openChat } = useChat()
  const location = useLocation()
  const isMobile = useIsMobile()
  const isLoginPage = location.pathname === '/login'
  

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-8 sticky top-0 z-50 flex-shrink-0">
      <nav className="max-w-7xl mx-auto bg-white/60 dark:bg-slate-800/60 backdrop-blur-3xl rounded-[28px] shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/50 dark:border-slate-700/50 ring-1 ring-black/[0.03] dark:ring-white/[0.03] overflow-visible">
        <div className="px-4 md:px-6 overflow-visible rounded-[28px]">
          <div className="flex items-center h-20 gap-2 md:gap-3 lg:gap-4 overflow-visible">
            {/* Logo - Full logo on desktop, icon on mobile */}
            <Link to="/" className="flex items-center space-x-2 flex-shrink-0 group">
              {isMobile ? (
                <LogoIcon className="h-8 w-8 transition-transform group-hover:scale-105" />
              ) : (
                <Logo className="h-10 w-auto transition-transform group-hover:scale-105" />
              )}
            </Link>

            {/* Navigation Links with Dropdowns */}
            <div className="flex items-center gap-2 sm:gap-3 md:gap-2 lg:gap-3 flex-1 justify-center px-2 md:px-0 min-w-0 overflow-hidden">
              <div className="flex items-center gap-2 sm:gap-3 md:gap-2 lg:gap-3 justify-center lg:justify-start overflow-hidden" style={{ flexWrap: 'nowrap' }}>
                {/* Platform Link */}
                <Link 
                  to="/" 
                  className="group relative flex items-center gap-1.5 md:gap-2 px-2.5 sm:px-3 md:px-3 py-2 md:py-3 rounded-2xl transition-all duration-300 outline-none focus:outline-none whitespace-nowrap text-slate-600 dark:text-slate-300 hover:text-[#69adba] dark:hover:text-[#69adba] font-medium flex-shrink-0"
                >
                  <div className="absolute inset-0 bg-white/40 dark:bg-slate-700/40 backdrop-blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Layers className="h-4 w-4 md:h-5 md:w-5 relative z-10 flex-shrink-0" />
                  <span className="text-sm relative z-10 hidden xl:inline ml-1">Platform</span>
                </Link>

                {/* Resources Link */}
                <Link 
                  to="/resources" 
                  className="group relative flex items-center gap-1.5 md:gap-2 px-2.5 sm:px-3 md:px-3 py-2 md:py-3 rounded-2xl transition-all duration-300 outline-none focus:outline-none whitespace-nowrap text-slate-600 dark:text-slate-300 hover:text-[#69adba] dark:hover:text-[#69adba] font-medium flex-shrink-0"
                >
                  <div className="absolute inset-0 bg-white/40 dark:bg-slate-700/40 backdrop-blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <BookOpen className="h-4 w-4 md:h-5 md:w-5 relative z-10 flex-shrink-0" />
                  <span className="text-sm relative z-10 hidden xl:inline ml-1">Resources</span>
                </Link>

                {/* Company Link */}
                <Link 
                  to="/company" 
                  className="group relative flex items-center gap-1.5 md:gap-2 px-2.5 sm:px-3 md:px-3 py-2 md:py-3 rounded-2xl transition-all duration-300 outline-none focus:outline-none whitespace-nowrap text-slate-600 dark:text-slate-300 hover:text-[#69adba] dark:hover:text-[#69adba] font-medium flex-shrink-0"
                >
                  <div className="absolute inset-0 bg-white/40 dark:bg-slate-700/40 backdrop-blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Building2 className="h-4 w-4 md:h-5 md:w-5 relative z-10 flex-shrink-0" />
                  <span className="text-sm relative z-10 hidden xl:inline ml-1">Company</span>
                </Link>

                {/* Legal Link */}
                <Link 
                  to="/legal" 
                  className="group relative flex items-center gap-1.5 md:gap-2 px-2.5 sm:px-3 md:px-3 py-2 md:py-3 rounded-2xl transition-all duration-300 outline-none focus:outline-none whitespace-nowrap text-slate-600 dark:text-slate-300 hover:text-[#69adba] dark:hover:text-[#69adba] font-medium flex-shrink-0"
                >
                  <div className="absolute inset-0 bg-white/40 dark:bg-slate-700/40 backdrop-blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Scale className="h-4 w-4 md:h-5 md:w-5 relative z-10 flex-shrink-0" />
                  <span className="text-sm relative z-10 hidden xl:inline ml-1">Legal</span>
                </Link>
              </div>
            </div>

            {/* Right side - Theme Toggle & Auth */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-2 lg:ml-4" style={{ minWidth: 'fit-content' }}>
              <ThemeToggle />
              {!isLoginPage && (
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-1.5 md:gap-2 border-slate-300 dark:border-slate-500 text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700/80 hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-[#69adba] dark:hover:border-[#69adba] shadow-sm dark:shadow-md dark:shadow-black/20 h-9 px-2 md:px-3"
                  >
                    <User className="h-4 w-4 flex-shrink-0" />
                    <span className="hidden md:inline">Sign In</span>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </div>
  )
}

